import { useState } from 'react'

import { Link, useNavigate } from '@tanstack/react-router'
import { useResendOnboardingDocument, useSendOnboardingDocument } from 'commons/hooks/bank/onboardingSetupDocuments'
import { Request } from 'commons/utils/request'
import { DownloadLink, IconName, Toast } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button, IconButton } from 'ui/components/Buttons'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'

import { handlerServerError } from '@orbit/errors'

import { CancelEsignLinkDocumentDialog } from '../../../../components/CancelEsignLinkDocumentDialog'
import { useDeleteDocument } from '../../../../hooks/useDeleteDocument'
import { ActionsProps } from './Actions.types'

export const Actions = ({ webPageData, applicationId }: ActionsProps) => {
  const navigate = useNavigate()
  const confirmation = useConfirmation()

  const { id, enableCancel, enableDelete, enableClone, enableResend, enableSend, enableDownload } = webPageData

  const { openDeleteConfirmation } = useDeleteDocument(id, () =>
    navigate({ to: '/onboarding/applications/$applicationId', params: { applicationId: applicationId.toString() } })
  )

  const { routine: sendOnboardingDocument } = useSendOnboardingDocument({
    onSuccess: async () => {
      navigate({ to: '/onboarding/applications/$applicationId', params: { applicationId: applicationId.toString() } })
      Toast.successSmall(`The set of onboarding documents ID-${id} has been sent`)
    },
    onError: (error) => handlerServerError(error)
  })

  const { routine: resendOnboardingDocument } = useResendOnboardingDocument({
    onSuccess: async () => {
      navigate({ to: '/onboarding/applications/$applicationId', params: { applicationId: applicationId.toString() } })
      Toast.successSmall(`The set of onboarding documents ID-${id} has been resent`)
    },
    onError: (error) => handlerServerError(error)
  })

  const handleDocumentSend = async () => {
    if (
      await confirmation.showAskConfirmation({
        message: <>Are you sure you want to&nbsp;send the set of&nbsp;documents&nbsp;ID-{id}?</>,
        submitButtonText: 'Send',
        confirmCallback: () => sendOnboardingDocument(id)
      })
    ) {
      await confirmation.showDoneConfirmation({
        message: <>The set of onboarding documents ID-{id} has been sent</>
      })
    }
  }

  const handleDocumentResend = async () => {
    if (
      await confirmation.showAskConfirmation({
        message: <>Are you sure you want to&nbsp;resend the set of&nbsp;documents&nbsp;ID-{id}?</>,
        submitButtonText: 'Resend',
        confirmCallback: () => resendOnboardingDocument(id)
      })
    ) {
      await confirmation.showDoneConfirmation({
        message: <>The set of onboarding documents ID-{id} has been resent</>
      })
    }
  }

  const [showCancelEsignDialog, setShowCancelEsignDialog] = useState(false)

  return (
    <ButtonGroup margin="big">
      {showCancelEsignDialog && (
        <CancelEsignLinkDocumentDialog
          id={id}
          onClose={() => setShowCancelEsignDialog(false)}
          onSuccess={() =>
            navigate({
              to: '/onboarding/applications/$applicationId',
              params: { applicationId: applicationId.toString() }
            })
          }
        />
      )}

      {enableDelete && (
        <IconButton face="neutral" icon={IconName.DELETE} onClick={() => openDeleteConfirmation()}>
          Delete
        </IconButton>
      )}
      {enableCancel && (
        <IconButton face="neutral" icon={IconName.CANCEL_WHITE_ROUND} onClick={() => setShowCancelEsignDialog(true)}>
          Cancel ESign Link
        </IconButton>
      )}
      {enableSend && (
        <Button face="inherit white" onClick={() => handleDocumentSend()}>
          Send
        </Button>
      )}
      {enableResend && (
        <Button face="inherit white" onClick={() => handleDocumentResend()}>
          Resend
        </Button>
      )}
      {enableClone && (
        <Link
          to="/application/$applicationId/onboarding-documents/$id/clone"
          params={{ applicationId: applicationId.toString(), id: id.toString() }}
        >
          <Button face="inherit white">Clone</Button>
        </Link>
      )}
      {enableDownload && (
        <DownloadLink
          direct
          baseUrl={Request.urls.bank.downloadOnboardingDocuments}
          parametersForm={{ appDocusignEnvelopeId: id }}
          handleError={() => ({})}
          withIcon={false}
        >
          <Button face="inherit white">Download</Button>
        </DownloadLink>
      )}
    </ButtonGroup>
  )
}
